/**
 * Component System Initializer
 *
 * Initializes the complete component rendering system by registering
 * the ComponentRenderer with the UnifiedLeadTracker form completion hooks.
 */

import type { ComponentRendererOptions, ComponentRenderingResult } from '../core'
import type { FormCompletionData } from '../integration'

import { ComponentRenderer } from '../core'
import { componentRendererRegistry, initializeComponentRendererRegistry } from '../integration'
import { detectEnvironment } from '../index'

/**
 * System initialization configuration
 */
export interface SystemInitConfig {
  /** Environment to use (auto-detected if not provided) */
  environment?: 'development' | 'production'
  /** Debug mode override */
  debugMode?: boolean
  /** Custom API headers */
  apiHeaders?: Record<string, string>
  /** Component renderer options */
  rendererOptions?: Partial<ComponentRendererOptions>
  /** Success callback for component rendering results */
  onRenderingComplete?: (result: ComponentRenderingResult) => void
  /** Error callback for system initialization errors */
  onInitializationError?: (error: string) => void
}

/**
 * Global component renderer instance
 */
let globalComponentRenderer: ComponentRenderer | null = null

/**
 * System initialization status
 */
let systemInitialized = false

/**
 * DEPRECATED: No longer needed - components hidden via inline CSS in Webflow
 *
 * Previously this function hid outcome components on page load via JavaScript.
 * This caused FOUC (Flash of Unstyled Content) issues, especially on:
 * - Slow mobile networks
 * - In-app browsers (Facebook, Instagram, etc.)
 * - When JavaScript fails to load
 *
 * Solution: Components now have display:none in inline CSS in Webflow page head.
 * JavaScript only needs to SHOW components (not hide them).
 * This is more reliable and prevents any flash of hidden content.
 */
// function hideAllComponentsOnLoad(debugMode: boolean): void {
//   // This function is no longer used - kept for reference
// }

/**
 * Initialize the complete component rendering system
 *
 * This function sets up the integration between UnifiedLeadTracker form
 * completion events and the component rendering system.
 */
export async function initializeComponentSystem(config: SystemInitConfig = {}): Promise<{
  success: boolean
  error?: string
  renderer?: ComponentRenderer
}> {
  if (systemInitialized) {
    return {
      success: true,
      renderer: globalComponentRenderer || undefined
    }
  }

  try {
    // Detect environment if not provided
    const environment = config.environment || detectEnvironment()
    const debugMode = config.debugMode !== undefined
      ? config.debugMode
      : environment === 'development'

    if (debugMode) {
      console.log('🚀 Initializing ProDebtCo Component Rendering System', {
        environment,
        debugMode
      })
    }

    // NOTE: Components are now hidden by default via inline CSS in Webflow
    // This prevents FOUC (Flash of Unstyled Content) even if JavaScript fails
    // No need to hide them via JavaScript anymore - CSS handles it reliably

    // Initialize the component renderer registry
    initializeComponentRendererRegistry(debugMode)

    // Create component renderer options
    const rendererOptions: ComponentRendererOptions = {
      environment,
      debugMode,
      apiHeaders: config.apiHeaders,
      ...config.rendererOptions
    }

    // Create the global component renderer instance
    globalComponentRenderer = new ComponentRenderer(rendererOptions)

    // Register the form completion handler
    componentRendererRegistry.onFormCompletion(async (data: FormCompletionData) => {
      if (!globalComponentRenderer) {
        if (debugMode) {
          console.error('🎯 Component renderer not initialized when form completion triggered')
        }
        return
      }

      try {
        if (debugMode) {
          console.log('🎯 Form completion event received, starting component rendering')
        }

        const result = await globalComponentRenderer.handleFormCompletion(data)

        if (debugMode) {
          console.log('🎯 Component rendering completed:', {
            success: result.success,
            scenarioCode: result.scenarioCode,
            componentsShown: result.componentsShown,
            componentsHidden: result.componentsHidden,
            usedFallback: result.usedFallback,
            processingTime: result.processingTime
          })
        }

        // Notify caller of completion
        config.onRenderingComplete?.(result)

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown rendering error'

        if (debugMode) {
          console.error('🎯 Component rendering failed:', error)
        }

        // Create error result
        const errorResult: ComponentRenderingResult = {
          success: false,
          error: errorMessage,
          componentsShown: [],
          componentsHidden: [],
          usedFallback: false,
          processingTime: 0
        }

        config.onRenderingComplete?.(errorResult)
      }
    })

    systemInitialized = true

    if (debugMode) {
      console.log('✅ Component rendering system initialized successfully')
    }

    return {
      success: true,
      renderer: globalComponentRenderer
    }

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown initialization error'

    if (config.debugMode) {
      console.error('❌ Component system initialization failed:', error)
    }

    config.onInitializationError?.(errorMessage)

    return {
      success: false,
      error: errorMessage
    }
  }
}

/**
 * Get the current component renderer instance
 */
export function getComponentRenderer(): ComponentRenderer | null {
  return globalComponentRenderer
}

/**
 * Check if the system is initialized
 */
export function isSystemInitialized(): boolean {
  return systemInitialized
}

/**
 * Shutdown the component system (cleanup)
 */
export function shutdownComponentSystem(): void {
  if (systemInitialized) {
    componentRendererRegistry.clearHandlers()
    globalComponentRenderer = null
    systemInitialized = false

    console.log('🛑 Component rendering system shut down')
  }
}

/**
 * Get system status information
 */
export function getSystemStatus() {
  return {
    initialized: systemInitialized,
    hasRenderer: globalComponentRenderer !== null,
    hasHandlers: componentRendererRegistry.hasHandlers(),
    rendererStatus: globalComponentRenderer?.getStatus()
  }
}